import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Pagination,
  CircularProgress, Button, Tooltip, LinearProgress, MenuItem, Select,
  FormControl, InputLabel, Skeleton,
} from "@mui/material";
import AddRoundedIcon          from "@mui/icons-material/AddRounded";
import VisibilityRoundedIcon   from "@mui/icons-material/VisibilityRounded";
import PlayArrowRoundedIcon    from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon        from "@mui/icons-material/PauseRounded";
import PlayCircleRoundedIcon   from "@mui/icons-material/PlayCircleRounded";
import LocalOfferRoundedIcon   from "@mui/icons-material/LocalOfferRounded";
import InboxRoundedIcon        from "@mui/icons-material/InboxRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import { useNavigate }         from "react-router-dom";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  orange:  "#FF7900", orangeLt:"#FFF0E0",
  amber:   "#D97706", amberLt: "#FEF3C7",
  emerald: "#059669", emeraldLt:"#D1FAE5",
  rose:    "#E11D48", roseLt:  "#FFE4E6",
  indigo:  "#4F46E5", indigoLt:"#EEF2FF",
  bg:      "#F7FAFC", border:  "#E2E8F0",
  text:    "#0F172A", muted:   "#64748B",
};

const STATUS_META = {
  draft:            { color:"#475569", bg:"#F1F5F9",  label:"Draft",            border:"#CBD5E1" },
  active:           { color:T.emerald, bg:T.emeraldLt, label:"Active",           border:T.emerald },
  paused:           { color:T.amber,   bg:T.amberLt,   label:"Paused",           border:T.amber   },
  budget_exhausted: { color:T.rose,    bg:T.roseLt,    label:"Budget Exhausted", border:T.rose    },
  expired:          { color:T.muted,   bg:"#F8FAFC",   label:"Expired",          border:T.border  },
};

const AUDIENCE_META = {
  all:      { color:T.teal,    bg:T.tealLt,   label:"All"      },
  customer: { color:T.teal,    bg:T.tealLt,   label:"Customer" },
  owner:    { color:T.indigo,  bg:T.indigoLt, label:"Owner"    },
  driver:   { color:T.emerald, bg:T.emeraldLt,label:"Driver"   },
};

const TYPE_LABELS = {
  promo_code:"Promo Code", first_booking:"First Booking", loyalty_tier:"Loyalty Tier",
  customer_referral:"Customer Referral", seasonal_route:"Seasonal / Route",
  trip_milestone:"Trip Milestone", capacity_fill:"Capacity Fill",
  rating_reward:"Rating Reward", early_delivery:"Early Delivery",
  owner_referral:"Owner Referral",
};

const STATUS_FILTERS = [
  { value:"",               label:"All",             color:T.muted  },
  { value:"active",         label:"Active",          color:T.emerald},
  { value:"draft",          label:"Draft",           color:"#475569"},
  { value:"paused",         label:"Paused",          color:T.amber  },
  { value:"budget_exhausted",label:"Budget Exhausted",color:T.rose  },
  { value:"expired",        label:"Expired",         color:T.muted  },
];

const StatusChip = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <Chip size="small" label={m.label}
    sx={{ bgcolor:m.bg,color:m.color,fontWeight:700,fontSize:11,height:22,border:`1px solid ${m.border}44` }} />;
};

export default function CampaignsList() {
  const navigate = useNavigate();
  const [rows,         setRows]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [page,         setPage]         = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [typeFilter,   setTypeFilter]   = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [busyId,       setBusyId]       = useState(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit:25 };
      if (typeFilter)   params.type   = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await getData(API_ENDPOINTS.dimCampaigns, params);
      const r = res?.result;
      if (r) { setRows(r.rows||[]); setTotalPages(r.totalPages||1); }
    } catch(e) { console.error("Campaigns fetch:", e); }
    setLoading(false);
  }, [page, typeFilter, statusFilter]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const lifecycleAction = async (id, action, body = {}) => {
    setBusyId(id);
    try {
      await postData(`/api/dim/admin/campaigns/${id}/${action}`, body);
      await fetchCampaigns();
    } catch(e) { alert(e.response?.data?.message || `${action} failed`); }
    finally { setBusyId(null); }
  };

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* ── Hero ── */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 55%,#005559 100%)`,
        borderRadius:"20px", p:3, mb:3,
        display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
        boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex",alignItems:"center",gap:2 }}>
          <Box sx={{ width:46,height:46,borderRadius:"12px",bgcolor:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center" }}>
            <LocalOfferRoundedIcon sx={{ fontSize:24,color:"#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>Campaigns</Typography>
            <Typography sx={{ fontSize:13,color:"rgba(255,255,255,0.75)",mt:0.3 }}>DIM-001 · Discounts &amp; Incentives</Typography>
          </Box>
        </Box>
        <Button variant="contained" disableElevation startIcon={<AddRoundedIcon />}
          onClick={()=>navigate("/app/campaigns/new")}
          sx={{ borderRadius:"12px",textTransform:"none",fontWeight:700,
            bgcolor:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.4)",
            "&:hover":{ bgcolor:"rgba(255,255,255,0.3)" } }}>
          New Campaign
        </Button>
      </Box>

      {/* ── Filters ── */}
      <Box sx={{ display:"flex",gap:1.5,mb:2.5,flexWrap:"wrap",alignItems:"center" }}>
        {/* Status pills */}
        <Box sx={{ display:"flex",gap:1,flexWrap:"wrap" }}>
          {STATUS_FILTERS.map(f => {
            const active = statusFilter === f.value;
            const m = STATUS_META[f.value] || { bg:T.border };
            return (
              <Box key={f.value} onClick={()=>{ setStatusFilter(f.value); setPage(1); }}
                sx={{ px:1.5,py:0.6,borderRadius:"10px",cursor:"pointer",fontWeight:700,fontSize:13,userSelect:"none",
                  bgcolor: active ? f.color : "#fff", color: active ? "#fff" : f.color,
                  border:`1.5px solid ${active ? f.color : f.color+"66"}`,
                  transition:"all 0.15s","&:hover":{ bgcolor:active ? f.color : m.bg||"#f8fafc" },
                }}>
                {f.label}
              </Box>
            );
          })}
        </Box>
        {/* Type select */}
        <FormControl size="small" sx={{ minWidth:180 }}>
          <InputLabel sx={{ fontSize:13 }}>Campaign Type</InputLabel>
          <Select value={typeFilter} label="Campaign Type"
            onChange={e=>{ setTypeFilter(e.target.value); setPage(1); }}
            sx={{ borderRadius:"12px",fontSize:13 }}>
            <MenuItem value="">All Types</MenuItem>
            {Object.entries(TYPE_LABELS).map(([k,v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* ── Table card ── */}
      <Card sx={{ borderRadius:"16px",border:`1px solid ${T.border}`,boxShadow:"0 1px 4px rgba(15,23,42,0.06)",overflow:"hidden" }}>
        <Box sx={{ height:3,background:`linear-gradient(90deg,${T.teal},${T.tealLt})` }} />

        {loading ? (
          <Box sx={{ p:2.5 }}>
            {Array.from({length:6}).map((_,i)=>(
              <Skeleton key={i} variant="rounded" height={52} sx={{ mb:1,borderRadius:"10px" }} />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ py:8,textAlign:"center" }}>
            <Box sx={{ width:60,height:60,borderRadius:"16px",bgcolor:T.tealLt,display:"inline-flex",alignItems:"center",justifyContent:"center",mb:2 }}>
              <InboxRoundedIcon sx={{ fontSize:30,color:T.teal }} />
            </Box>
            <Typography sx={{ fontSize:15,fontWeight:700,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>No campaigns found</Typography>
            <Typography sx={{ fontSize:13,color:T.muted,mt:0.5,mb:2.5 }}>
              Create your first promo, loyalty, or incentive campaign to get started.
            </Typography>
            <Button variant="contained" disableElevation startIcon={<AddRoundedIcon />}
              onClick={()=>navigate("/app/campaigns/new")}
              sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:T.teal,"&:hover":{ bgcolor:T.tealDk } }}>
              Create First Campaign
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& .MuiTableCell-root":{
                  bgcolor:"#F8FAFC",fontWeight:700,fontSize:11,
                  textTransform:"uppercase",letterSpacing:"0.4px",color:T.muted,
                  py:1.25,borderBottom:`2px solid ${T.border}`,
                }}}>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Budget</TableCell>
                  <TableCell>Window</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(c => {
                  const used = Number(c.budget_used);
                  const cap  = Number(c.budget_cap);
                  const pct  = cap > 0 ? Math.min(100,(used/cap)*100) : 0;
                  const aud  = AUDIENCE_META[c.audience] || AUDIENCE_META.all;

                  return (
                    <TableRow key={c.campaign_id} hover
                      sx={{ cursor:"pointer","&:hover":{ bgcolor:"#F8FAFC" },"& .MuiTableCell-root":{ py:1.5,borderBottom:`1px solid ${T.border}` } }}
                      onClick={()=>navigate(`/app/campaigns/${c.campaign_id}`)}>

                      <TableCell>
                        <Typography sx={{ fontSize:13,fontWeight:700,color:T.text }}>{c.name}</Typography>
                        <Box sx={{ px:0.8,py:0.2,bgcolor:T.tealLt,borderRadius:"5px",display:"inline-block",mt:0.3 }}>
                          <Typography sx={{ fontFamily:"monospace",fontSize:11,color:T.teal }}>{c.code}</Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontSize:12,fontWeight:600,color:T.muted }}>
                          {TYPE_LABELS[c.type] || c.type}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Chip size="small" label={aud.label}
                          sx={{ bgcolor:aud.bg,color:aud.color,fontWeight:700,fontSize:11,height:22,border:`1px solid ${aud.color}33` }} />
                      </TableCell>

                      <TableCell><StatusChip status={c.status} /></TableCell>

                      <TableCell sx={{ minWidth:160 }}>
                        <Typography sx={{ fontSize:12,color:T.text,fontWeight:600 }}>
                          ₹{used.toLocaleString()} <Box component="span" sx={{ color:T.muted,fontWeight:400 }}>/ ₹{cap.toLocaleString()}</Box>
                        </Typography>
                        <LinearProgress variant="determinate" value={pct}
                          sx={{ mt:0.6,height:5,borderRadius:3,bgcolor:"#F0F4F8",
                            "& .MuiLinearProgress-bar":{ borderRadius:3,
                              bgcolor: pct>=80 ? T.rose : pct>=50 ? T.amber : T.teal } }} />
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display:"flex",alignItems:"center",gap:0.5,mb:0.3 }}>
                          <CalendarTodayRoundedIcon sx={{ fontSize:11,color:T.muted }} />
                          <Typography sx={{ fontSize:11,color:T.text }}>
                            {new Date(c.start_at).toLocaleDateString("en-IN")}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize:11,color:T.muted,ml:"15px" }}>
                          → {new Date(c.expires_at).toLocaleDateString("en-IN")}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" onClick={e=>e.stopPropagation()}>
                        <Box sx={{ display:"flex",justifyContent:"flex-end",gap:0.5 }}>
                          <Tooltip title="View / Edit">
                            <IconButton size="small"
                              onClick={()=>navigate(`/app/campaigns/${c.campaign_id}`)}
                              sx={{ color:T.teal,bgcolor:T.tealLt,borderRadius:"8px","&:hover":{ bgcolor:"#b2e8ea" } }}>
                              <VisibilityRoundedIcon sx={{ fontSize:15 }} />
                            </IconButton>
                          </Tooltip>
                          {c.status === "draft" && (
                            <Tooltip title="Activate">
                              <IconButton size="small" disabled={busyId===c.campaign_id}
                                onClick={()=>lifecycleAction(c.campaign_id,"activate")}
                                sx={{ color:T.emerald,bgcolor:T.emeraldLt,borderRadius:"8px","&:hover":{ bgcolor:"#a7f3d0" } }}>
                                <PlayArrowRoundedIcon sx={{ fontSize:15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {c.status === "active" && (
                            <Tooltip title="Pause">
                              <IconButton size="small" disabled={busyId===c.campaign_id}
                                onClick={()=>lifecycleAction(c.campaign_id,"pause",{ reason:"manual" })}
                                sx={{ color:T.amber,bgcolor:T.amberLt,borderRadius:"8px","&:hover":{ bgcolor:"#FDE68A" } }}>
                                <PauseRoundedIcon sx={{ fontSize:15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {c.status === "paused" && (
                            <Tooltip title="Resume">
                              <IconButton size="small" disabled={busyId===c.campaign_id}
                                onClick={()=>lifecycleAction(c.campaign_id,"resume")}
                                sx={{ color:T.indigo,bgcolor:T.indigoLt,borderRadius:"8px","&:hover":{ bgcolor:"#c7d2fe" } }}>
                                <PlayCircleRoundedIcon sx={{ fontSize:15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {totalPages > 1 && (
        <Box sx={{ display:"flex",justifyContent:"center",mt:3 }}>
          <Pagination count={totalPages} page={page} onChange={(_,p)=>setPage(p)}
            sx={{ "& .MuiPaginationItem-root":{ borderRadius:"10px" },
                  "& .Mui-selected":{ bgcolor:`${T.teal} !important`,color:"#fff" } }} />
        </Box>
      )}
    </Box>
  );
}
